import { useEffect, useRef, useState } from "react";
import { X, ArrowRight, ArrowLeft, Compass } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export const tourStorageKey = "cos_tour_done_v1";

const STEPS = [
  {
    title: "Welcome to MITIVISE",
    body: "This 60-second tour shows you where everything lives. Skip anytime — you can replay it from this dashboard whenever you like.",
    target: null,
  },
  {
    title: "Your Compliance Readiness Indicator",
    body: "A living score of how ready your business is to prove compliance, built from your risks, documents, SOPs and audits. It updates as your records stay current.",
    target: '[data-testid="dashboard-readiness"]',
  },
  {
    title: "Alerts that need you",
    body: "These cards surface critical risks, open audits, documents expiring soon and SOPs awaiting review. Click any card to jump straight to it.",
    target: '[data-testid="kpi-critical-risks"]',
  },
  {
    title: "Start tasks in one click",
    body: "Add a risk, log an audit, create an SOP, upload a document or request TSM help — all from these quick actions.",
    target: '[data-testid="dashboard-quick-actions"]',
  },
  {
    title: "Everything in the sidebar",
    body: "Your registers, document center, the TSM services marketplace, downloadable toolkit files and billing all live in the left menu.",
    target: '[data-testid="app-sidebar-nav"]',
  },
  {
    title: "You're all set",
    body: "Keep your records current and your readiness score does the proof-reading for you. Need hands-on help? Request a TSM service anytime.",
    target: null,
  },
];

const posFor = (rect) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(370, vw - 24);
  if (!rect) return { left: (vw - cardW) / 2, top: vh / 2 - 140, cardW };
  const below = rect.bottom + 14 + 260 < vh;
  const left = Math.min(Math.max(12, rect.left + rect.width / 2 - cardW / 2), vw - cardW - 12);
  const top = below ? rect.bottom + 14 : Math.max(12, rect.top - 272);
  return { left, top: Math.min(Math.max(top, 12), vh - 268), cardW };
};

export const DashboardTour = ({ open, onDone }) => {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const targetRef = useRef(null);

  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  useEffect(() => {
    if (!open) {
      targetRef.current = null;
      return undefined;
    }
    const sel = STEPS[idx]?.target;
    const el = sel ? document.querySelector(sel) : null;
    targetRef.current = el;
    const capture = () => {
      if (!targetRef.current) {
        setRect(null);
        return;
      }
      const r = targetRef.current.getBoundingClientRect();
      setRect(r.width > 0 && r.height > 0 ? { top: r.top, left: r.left, width: r.width, height: r.height } : null);
    };
    const t = setTimeout(() => {
      if (el) el.scrollIntoView({ block: "center", inline: "nearest" });
      capture();
    }, 60);
    window.addEventListener("resize", capture);
    window.addEventListener("scroll", capture, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", capture);
      window.removeEventListener("scroll", capture, true);
    };
  }, [idx, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onDone]);

  if (!open) return null;

  const step = STEPS[idx];
  const { left, top, cardW } = posFor(rect);
  const isLast = idx === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="tour-overlay">
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-lg border-2 border-gold transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(7, 27, 47, 0.78)",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-navy/80" />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Product tour"
        data-testid="tour-card"
        className="absolute rounded-xl bg-white p-6 shadow-2xl"
        style={{ left, top, width: cardW }}
      >
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-golddark">
            <Compass className="h-3.5 w-3.5" />
            <span data-testid="tour-step-indicator">Tour · {idx + 1} of {STEPS.length}</span>
          </span>
          <button
            type="button"
            onClick={onDone}
            aria-label="Skip tour"
            data-testid="tour-skip-button"
            className="rounded p-1 text-ink/40 transition-colors hover:text-navy"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {idx === 0 && <LogoMark className="mt-4 h-10 w-10" />}
        <h3 className="mt-3 font-serif text-2xl font-semibold text-navy" data-testid="tour-title">
          {step.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-ink/70">{step.body}</p>
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            data-testid="tour-back-button"
            className="inline-flex items-center gap-1.5 rounded-sm border border-navy/15 px-4 py-2 text-sm font-semibold text-navy/70 transition-colors hover:border-gold disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            type="button"
            onClick={() => (isLast ? onDone() : setIdx((i) => i + 1))}
            data-testid={isLast ? "tour-finish-button" : "tour-next-button"}
            className="btn-gold"
          >
            {isLast ? "Finish" : "Next"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
